'use client';

import Link from 'next/link';
import { Reveal } from '@/components/products/Reveal';
import {
  AccentGlow,
  CtaRow,
  DemoBand,
  FeatureRows,
  ProductMarquee,
  SectionHead,
  StatGrid,
} from '@/components/products/blocks';
import { MonthlyChart } from './MonthlyChart';
import {
  beforeAfterCaption,
  beforeAfterPairs,
  chartMonths,
  dailyLoop,
  gayaArtis,
  gayaMonthly,
  heroCaption,
  managedBrands,
  monthlyCheckpoints,
  pricing,
  proofDetails,
  proofStats,
  reportLinkLabel,
  serviceRows,
  swanChartCaption,
  swanMonthly,
} from './socialAiData';

const percentFormat = (n: number) => `${n.toFixed(1)}%`;

const ACCENT = 'var(--color-accent)';

function formatNumber(n: number) {
  return n.toLocaleString('en-US');
}

/** One inline before/after bar per metric, normalized to its own after-value. */
function BeforeAfterBars() {
  return (
    <div className="grid gap-7">
      {beforeAfterPairs.map((pair) => {
        const beforeWidth = Math.max((pair.before / pair.after) * 100, 1.5);
        return (
          <div key={pair.label}>
            <div className="flex items-baseline justify-between gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
              <span>{pair.label}</span>
              <span className="text-white/70">
                {formatNumber(pair.before)} → {formatNumber(pair.after)}
              </span>
            </div>
            <div className="mt-2.5 flex h-2.5 items-center gap-1.5 rounded-full bg-white/[0.06]">
              <div
                className="h-full rounded-full bg-white/20"
                style={{ width: `${beforeWidth}%` }}
                aria-hidden="true"
              />
            </div>
            <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-white/[0.06]">
              <div
                className="h-full rounded-full"
                style={{ width: '100%', backgroundColor: ACCENT }}
                aria-hidden="true"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function SocialAiPage() {
  return (
    <div className="self-styled relative text-white">
      {/* ── Hero (split: copy left, typographic proof right) ─────────── */}
      <section className="relative flex min-h-[100dvh] flex-col justify-center overflow-hidden px-6 pb-16 pt-[clamp(7rem,12vw,9rem)] md:px-12 lg:px-24">
        <div className="mx-auto grid w-full max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <nav
              aria-label="Breadcrumb"
              className="pv-load font-mono text-[10px] uppercase tracking-[0.25em] text-white/40"
            >
              <Link href="/services" className="transition-colors duration-300 hover:text-white">
                Services
              </Link>
              <span className="mx-3" aria-hidden="true">
                /
              </span>
              <span style={{ color: ACCENT }}>AI social media</span>
            </nav>
            <h1
              className="pv-load mt-7 font-six-caps text-[clamp(3.2rem,6.5vw,6.5rem)] lowercase leading-[0.9] text-white"
              style={{ animationDelay: '100ms' }}
            >
              social media that brings customers, not likes.
            </h1>
            <p
              className="pv-load mt-7 max-w-lg text-base font-light leading-relaxed text-white/65 md:text-lg"
              style={{ animationDelay: '220ms' }}
            >
              AI-native management, proven with real numbers, for Iraqi resorts, restaurants, shops and
              education brands.
            </p>
            <div className="pv-load" style={{ animationDelay: '340ms' }}>
              <CtaRow
                primary={{ label: 'Book a strategy call', href: '/contact' }}
                accent={ACCENT}
                className="mt-10"
              />
            </div>
            <p
              className="pv-load mt-7 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40"
              style={{ animationDelay: '460ms' }}
            >
              Managed since June 2026 · Erbil
            </p>
          </div>
          <div className="pv-load relative lg:col-span-6" style={{ animationDelay: '300ms' }}>
            <AccentGlow accent={ACCENT} className="left-1/4 top-1/4 h-80 w-80" />
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] px-8 py-12 text-center md:px-12 md:py-16">
              <p className="font-six-caps text-[clamp(2.6rem,7vw,5.5rem)] leading-[0.9] text-white">
                {heroCaption.before}
                <span style={{ color: ACCENT }}> → </span>
                {heroCaption.after}
              </p>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                {heroCaption.detail}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Proof (stats + one before/after bar block + Gaya) ─────────── */}
      <section aria-label="What changed" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead kicker="What changed" accent={ACCENT} title="the numbers, not the vibe." />
          </Reveal>
          <Reveal delay={100}>
            <p className="mt-14 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
              Swan Village · since June 2026
            </p>
            <StatGrid accent={ACCENT} stats={proofStats} className="mt-5" />
          </Reveal>

          <Reveal delay={160} className="mt-16 max-w-3xl">
            <BeforeAfterBars />
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
              {beforeAfterCaption}
            </p>
          </Reveal>

          <Reveal delay={200} className="mt-12 max-w-3xl">
            <ul className="grid gap-2.5 font-mono text-[11px] leading-relaxed text-white/50 sm:grid-cols-2">
              {proofDetails.map((detail) => (
                <li key={detail} className="flex gap-2.5">
                  <span style={{ color: ACCENT }} aria-hidden="true">
                    ·
                  </span>
                  {detail}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={200} className="mt-16">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">Month by month</p>

            <div className="mt-6 grid gap-x-8 gap-y-10 sm:grid-cols-2 sm:divide-x sm:divide-white/10">
              <MonthlyChart
                title="IG accounts reached"
                months={chartMonths}
                values={swanMonthly.igAccountsReached}
                startIndex={2}
                className="sm:pr-8"
              />
              <MonthlyChart
                title="IG views"
                months={chartMonths}
                values={swanMonthly.igViews}
                startIndex={2}
                className="sm:pl-8"
              />
            </div>
            <div className="mt-10 grid gap-x-8 gap-y-10 border-t border-white/10 pt-10 sm:grid-cols-2 sm:divide-x sm:divide-white/10">
              <MonthlyChart
                title="FB video views"
                months={chartMonths}
                values={swanMonthly.fbVideoViews}
                startIndex={2}
                className="sm:pr-8"
              />
              <MonthlyChart
                title="FB post engagements"
                months={chartMonths}
                values={swanMonthly.fbPostEngagements}
                startIndex={2}
                className="sm:pl-8"
              />
            </div>

            <div className="mt-10 grid gap-x-6 gap-y-10 border-t border-white/10 pt-10 xl:grid-cols-3 xl:divide-x xl:divide-white/10">
              <MonthlyChart
                title="IG profile visits"
                months={chartMonths}
                values={swanMonthly.igProfileVisits}
                startIndex={2}
                height={160}
                className="xl:pr-6"
              />
              <MonthlyChart
                title="FB new followers"
                months={chartMonths}
                values={swanMonthly.fbNewFollowers}
                startIndex={2}
                height={160}
                className="xl:px-6"
              />
              <MonthlyChart
                title="TikTok video plays"
                months={chartMonths}
                values={swanMonthly.tiktokVideoPlays}
                startIndex={2}
                height={160}
                note="Spark Ads, paid"
                className="xl:pl-6"
              />
            </div>

            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
              {swanChartCaption}
            </p>
          </Reveal>

          {/* Gaya Artis */}
          <Reveal delay={100} className="mt-20 rounded-2xl border border-white/10 bg-white/[0.03] p-7 md:p-10">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
              Gaya Artis · since December 2025
            </span>
            <p className="mt-5 font-six-caps text-4xl lowercase leading-none text-white md:text-5xl">
              {gayaArtis.headline}
            </p>
            <div className="mt-6">
              <ul className="grid gap-2.5 font-mono text-[11px] leading-relaxed text-white/55">
                {gayaArtis.details.map((detail, index) => (
                  <li key={detail}>
                    {detail}
                    {index === 0 && (
                      <span className="ml-3 whitespace-nowrap uppercase tracking-[0.2em] text-white/40">
                        {gayaArtis.window}
                      </span>
                    )}
                  </li>
                ))}
                <li className="text-white/70">{gayaArtis.sinceStart}</li>
              </ul>
            </div>

            <div className="mt-8 grid gap-x-6 gap-y-10 border-t border-white/10 pt-8 xl:grid-cols-3 xl:divide-x xl:divide-white/10">
              <MonthlyChart
                title="Conversations started"
                months={chartMonths}
                values={gayaMonthly.conversationsStarted}
                height={160}
                className="xl:pr-6"
              />
              <MonthlyChart
                title="People reached"
                months={chartMonths}
                values={gayaMonthly.peopleReached}
                height={160}
                className="xl:px-6"
              />
              <MonthlyChart
                title="Click-through rate"
                months={chartMonths}
                values={gayaMonthly.clickThroughRate}
                format={percentFormat}
                height={160}
                className="xl:pl-6"
              />
            </div>
          </Reveal>

          <Reveal delay={140}>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
              Catz Restaurant · Exchange Programmes · Asco Village — reports in the next edition.
            </p>
          </Reveal>

          <Reveal delay={100} className="mt-10">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white/60 transition-colors duration-300 hover:text-white"
            >
              {reportLinkLabel} →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── The 8 service rows ────────────────────────────────────────── */}
      <section aria-label="What we do" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={ACCENT}
              title="eight systems, working every day"
              lede="Every account gets the same eight-part discipline — nothing here is one-off content."
            />
          </Reveal>
          <Reveal delay={120}>
            <FeatureRows accent={ACCENT} rows={serviceRows} className="mt-14" />
          </Reveal>
        </div>
      </section>

      {/* ── How it runs (centered stack) ─────────────────────────────── */}
      <section aria-label="How it runs" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={ACCENT}
              title="how it runs"
              className="mx-auto text-center"
              titleClassName="mx-auto"
            />
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
              Monthly checkpoints
            </p>
            <div className="mx-auto mt-8 grid max-w-4xl gap-8 sm:grid-cols-3 sm:divide-x sm:divide-white/10">
              {monthlyCheckpoints.map((checkpoint, i) => (
                <div key={checkpoint.month} className="px-0 text-center sm:px-8">
                  <span
                    className="font-mono text-[10px] tracking-[0.2em]"
                    style={{ color: ACCENT }}
                  >
                    {String(i + 1).padStart(2, '0')} / {String(monthlyCheckpoints.length).padStart(2, '0')}
                  </span>
                  <p className="mt-3 text-lg font-light text-white">{checkpoint.month}</p>
                  <p className="mt-2 text-sm font-light leading-relaxed text-white/55">{checkpoint.body}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={200}>
            <p className="mx-auto mt-12 max-w-2xl text-center font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-white/45">
              {dailyLoop}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── The one marquee ──────────────────────────────────────────── */}
      <ProductMarquee accent={ACCENT} items={managedBrands} />

      {/* ── Pricing band (full-bleed, not a package table) ───────────── */}
      <section aria-label="Pricing" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
              Pricing
            </span>
            <p className="mt-5 font-six-caps text-6xl lowercase leading-[0.9] text-white md:text-7xl">
              {pricing.headline}
            </p>
            <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-white/55 md:text-base">
              {pricing.closingLine}
            </p>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7">
            <div className="border-t border-white/10">
              {pricing.items.map((item) => (
                <div key={item.title} className="border-b border-white/10 py-7">
                  <h3 className="text-lg font-medium text-white md:text-xl">{item.title}</h3>
                  <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-white/55">{item.body}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA band ──────────────────────────────────────────────────── */}
      <div className="px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <DemoBand
            ghost="social"
            eyebrow="Book a strategy call"
            title="the next report should be about you"
            sub="Bring your accounts as they are. We'll audit them on the call and tell you honestly what a 3-month plan would look like."
            primary={{ label: 'Book a strategy call', href: '/contact' }}
            accent={ACCENT}
          />
        </div>
      </div>
    </div>
  );
}
